import { useRef, useEffect } from 'react';

import { IconComponent } from '../../../../util/icons';
import vendorFeedPhoto from '../../../../asset/images/appfolio/Appfolio-feed.png';
import vendorSyncProperty from '../../../../asset/images/appfolio/Appfolio-Sync.png';
import appfolioStep1 from '../../../../asset/images/appfolio/AppFolio-Step1.png';
import appfolioStep2 from '../../../../asset/images/appfolio/AppFolio-Step2.png';
import appfolioStep3 from '../../../../asset/images/appfolio/AppFolio-Step3.png';
import appfolioStep4 from '../../../../asset/images/appfolio/AppFolio-Step4.png';
import appfolioStep5 from '../../../../asset/images/appfolio/AppFolio-Step5.png';
import appfolioStep6 from '../../../../asset/images/appfolio/AppFolio-Step6.png';
import appfolioStep7 from '../../../../asset/images/appfolio/AppFolio-Step7.png';
import appfolioStep8 from '../../../../asset/images/appfolio/AppFolio-Step8.png';
import appfolioStep10 from '../../../../asset/images/appfolio/AppFolio-Step10.jpeg';
import appfolioStep11 from '../../../../asset/images/appfolio/AppFolio-Step11.jpeg';
import appfolioStep12 from '../../../../asset/images/appfolio/AppFolio-Step12.jpeg';
import appfolioStep13 from '../../../../asset/images/appfolio/AppFolio-Step13.jpeg';
import appfolioStep14 from '../../../../asset/images/appfolio/AppFolio-Step14.jpeg';
import appfolioStep15 from '../../../../asset/images/appfolio/AppFolio-Step15.jpeg';

import { Typography } from '@/components/ui/typography';

interface StepData {
  step: number;
  description: React.ReactNode;
  image?: string;
  imageAlt?: string;
}

const Bold = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="heading-md" className="font-bold">{children}</Typography>
);

const Normal = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="heading-md" className="font-normal">{children}</Typography>
);

const STEPS: StepData[] = [
  {
    step: 1,
    description: <><Normal>Sign in to your </Normal><Bold>AppFolio</Bold><Normal> account</Normal></>,
    image: appfolioStep1,
    imageAlt: 'Sign in to AppFolio',
  },
  {
    step: 2,
    description: 'Click "Add Functionality"',
    image: appfolioStep2,
    imageAlt: 'Add Functionality',
  },
  {
    step: 3,
    description: 'Click "Integrations"',
    image: appfolioStep3,
    imageAlt: 'Integrations',
  },
  {
    step: 4,
    description: <><Normal>Search for </Normal><Bold>"PlanOmatic"</Bold></>,
    image: appfolioStep4,
    imageAlt: 'Search PlanOmatic',
  },
  {
    step: 5,
    description: <><Normal>Click </Normal><Bold>PlanOmatic</Bold><Normal> tile</Normal></>,
    image: appfolioStep5,
    imageAlt: 'PlanOmatic tile',
  },
  {
    step: 6,
    description: 'Click "Setup Connection"',
    image: appfolioStep6,
    imageAlt: 'Setup Connection',
  },
  {
    step: 7,
    description: <><Normal>Check "</Normal><Bold>Enable</Bold><Normal>" and</Normal><Bold> "All Properties"</Bold><Normal>, then click "Save"</Normal></>,
    image: appfolioStep7,
    imageAlt: 'Enable and All Properties',
  },
  {
    step: 8,
    description: 'Click "Enable Connection"',
    image: appfolioStep8,
    imageAlt: 'Enable Connection',
  },
  {
    step: 9,
    description: <><Normal>Login to </Normal><Bold>PlanOmatic</Bold></>,
  },
  {
    step: 10,
    description: 'Click "Manage Integrations"',
    image: appfolioStep10,
    imageAlt: 'Manage Integrations',
  },
  {
    step: 11,
    description: 'Click "Enable"',
    image: appfolioStep11,
    imageAlt: 'Enable',
  },
  {
    step: 12,
    description: 'Enter AppFolio Subdomain',
    image: appfolioStep12,
    imageAlt: 'AppFolio Subdomain',
  },
  {
    step: 13,
    description: 'Manage Integration Configuration',
    image: appfolioStep13,
    imageAlt: 'Integration Configuration',
  },
  {
    step: 14,
    description: 'Click "Save and Sync Properties"',
    image: appfolioStep14,
    imageAlt: 'Save and Sync Properties',
  },
  {
    step: 15,
    description: <Bold>Watch your properties magically appear! 🪄</Bold>,
    image: appfolioStep15,
    imageAlt: 'Properties synced',
  },
];

const renderStepDescription = (step: StepData) => {
  if (typeof step.description === 'string') {
    return (
      <>
        <Bold>Step {step.step}: </Bold>
        <Normal>{step.description}</Normal>
      </>
    );
  }
  return (
    <>
      <Bold>Step {step.step}: </Bold>
      {step.description}
    </>
  );
};

export const IntegrationOverviewAppfolio = (props: {
  isHowToConfigLinkClicked: boolean;
}) => {
  const howToConfigureRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (props.isHowToConfigLinkClicked) {
      howToConfigureRef?.current?.scrollIntoView();
    }
  }, [props.isHowToConfigLinkClicked]);

  return (
    <>
      <div className="flex flex-col gap-6 py-6">
        <div className="flex items-center gap-6 p-4">
          <div className="flex flex-col gap-3 flex-1">
            <Typography variant="heading-lg" className="font-medium">Sync your property data to PlanOmatic.</Typography>
            <Typography className="text-lg font-medium text-muted-foreground">Track which vacancies already have marketing photos, and place photo orders directly from the PlanOmatic platform with one click.</Typography>
          </div>
          <div>
            <IconComponent src={vendorSyncProperty} width={450} height={390} alt="Vendor Feed" />
          </div>
        </div>
        <div className="flex items-center gap-6 p-4">
          <div>
            <IconComponent src={vendorFeedPhoto} width={450} height={390} alt="Vendor Sync Property" />
          </div>
          <div className="ml-auto flex flex-col gap-3">
            <Typography variant="heading-lg" className="font-medium">Feed your listing media to AppFolio automatically.</Typography>
            <Typography className="text-lg font-medium text-muted-foreground">As soon as the order is ready, the assets are automatically delivered to AppFolio!</Typography>
          </div>
        </div>
      </div>

      <div ref={howToConfigureRef} className="flex flex-col gap-4 py-6">
        <Typography variant="heading-2xl" className="font-semibold">Instructions to Enable Integration</Typography>
        <Typography className="text-lg font-semibold">Follow the step-by-step guide below to enable your PlanOmatic & AppFolio integration</Typography>
        {STEPS.map((step) => (
          <div key={step.step}>
            <div className="flex items-center gap-1 flex-wrap">{renderStepDescription(step)}</div>
            {step.image && (
              <div className="mt-3 border rounded-md overflow-hidden inline-block">
                <IconComponent src={step.image} width={700} height={393} alt={step.imageAlt ?? ''} />
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};
